import React, { useState, useEffect } from "react";
import { Button, Grid, TextField, LinearProgress, Box, InputAdornment, Typography } from "@material-ui/core";
import { useSnackbar } from "notistack";
import { Club } from "api/clubs";
import { OwnerClubSettings, getSettings, patchSettings } from "api/owner/settings";
import { GridTextField } from "components";
import i18n from "../i18n";
import ChangeClubVisibilityDialog from "./ChangeClubVisibilityDialog";
import TransferOwnershipDialog from "./TransferOwnershipDialog";
import DeleteClubDialog from "./DeleteClubDialog";
import StripeSection from "./StripeSection";
import EmbedDashboardDialog from "./EmbedDashboardDialog";
import Covid19Dialog from "./Covid19Dialog";

// prop types
interface OwnershipTabProps {
    club?: Club,
    onFetchUserProfile: () => void,
}

// general club settings tab
const OwnershipTab = ({ club, onFetchUserProfile }: OwnershipTabProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [clubSettings, setClubSettings] = useState<OwnerClubSettings>();
    const [isLoading, setLoading] = useState(false);
    const [readyToSetupStripe, setReadyToSetupStripe] = useState(false);
    const [showChangeClubVisibilityDialog, setShowChangeClubVisibilityDialog] = useState(false);
    const [showCovid19Dialog, setShowCovid19Dialog] = useState(false);
    const [showDeleteClubDialog, setShowDeleteClubDialog] = useState(false);
    const [showEmbedDashboardDialog, setShowEmbedDashboardDialog] = useState(false);
    const [showTransferOwnershipDialog, setShowTransferOwnershipDialog] = useState(false);

    // save action
    const saveAction = async () => {
        setLoading(true);
        try {
            const updatedSettings = await patchSettings({
                clubId: club!.id,
                memberTaxRate: clubSettings!.memberTaxRate,
                taxNumber: clubSettings!.taxNumber || undefined,
                taxRate: clubSettings!.taxRate,
            });
            setClubSettings(updatedSettings);
            if (updatedSettings.taxNumber) setReadyToSetupStripe(true);
            enqueueSnackbar(i18n.savedSuccessfully, { variant: "success" });
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // fetch club settings
    const fetchClubSettings = async () => {
        const settings = await getSettings({ clubId: club!.id });
        setClubSettings(settings);
        if (settings.taxNumber) setReadyToSetupStripe(true);
    };
    useEffect(() => { if (club) fetchClubSettings(); }, [club]); // eslint-disable-line

    // render
    const memberTaxRate = clubSettings?.memberTaxRate || 0;
    const taxRate = clubSettings?.taxRate || 0;
    return (
        <>
            <Box style={{ alignItems: "center", display: "flex", flexDirection: "column", padding: "16px" }}>
                <Grid container spacing={2} justifyContent="center">

                    {/* taxes */}
                    <Grid item xs={12}>
                        <Typography align="center" variant="subtitle2">{i18n.taxes}</Typography>
                    </Grid>
                    <GridTextField
                        label={i18n.taxNumber}
                        size={4}
                        value={clubSettings?.taxNumber || ""}
                        onChange={(v) => setClubSettings({ ...clubSettings!, taxNumber: v })}
                    />
                    <Grid item xs={4}>
                        <TextField
                            fullWidth
                            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                            label={i18n.taxRate}
                            placeholder="0"
                            type="number"
                            value={Math.round(taxRate * 100)}
                            variant="outlined"
                            onBlur={() => (taxRate < 0 || taxRate > 1) && setClubSettings({ ...clubSettings!, taxRate: 0 })}
                            onChange={(e) => setClubSettings({ ...clubSettings!, taxRate: parseInt(e.target.value, 10) / 100 })}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            fullWidth
                            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                            label={i18n.memberTaxRate}
                            placeholder="0"
                            type="number"
                            value={Math.round(memberTaxRate * 100)}
                            variant="outlined"
                            onBlur={() => (memberTaxRate < 0 || memberTaxRate > 1) && setClubSettings({ ...clubSettings!, memberTaxRate: 0 })}
                            onChange={(e) => setClubSettings({ ...clubSettings!, memberTaxRate: parseInt(e.target.value, 10) / 100 })}
                        />
                    </Grid>
                    {isLoading && (
                        <Grid item xs={12}>
                            <LinearProgress />
                        </Grid>
                    )}
                    <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
                        <Button
                            color="primary"
                            disabled={isLoading}
                            variant="contained"
                            onClick={saveAction}
                        >
                            {i18n.save}
                        </Button>
                    </Grid>

                    {/* stripe */}
                    <Grid item xs={12} style={{ marginTop: "32px" }}>
                        <Typography align="center" variant="subtitle2">{i18n.stripe}</Typography>
                    </Grid>
                    <StripeSection
                        club={club}
                        clubSettings={clubSettings}
                        readyToSetupStripe={readyToSetupStripe}
                        onFetchUserProfile={onFetchUserProfile}
                    />

                    {/* covid-19 */}
                    <Grid item xs={12} style={{ marginTop: "32px" }}>
                        <Typography align="center" variant="subtitle2">{i18n.covid19}</Typography>
                    </Grid>
                    <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
                        <Button variant="contained" color="primary" onClick={() => setShowCovid19Dialog(true)}>
                            {i18n.identifyContacts}
                        </Button>
                    </Grid>

                    {/* danger zone */}
                    {/* https://open.spotify.com/track/34x6hEJgGAOQvmlMql5Ige?si=w_2tc0ZPTI-JGgTlLH8pRg */}
                    <Grid item xs={12} style={{ marginTop: "32px" }}>
                        <Typography align="center" variant="subtitle2">{i18n.dangerZone}</Typography>
                    </Grid>
                    <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
                        <Button variant="contained" onClick={() => setShowEmbedDashboardDialog(true)}>
                            {i18n.embedDashboard}
                        </Button>
                    </Grid>
                    {club?.clubStatus !== "demo" && (
                        <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
                            <Button variant="contained" onClick={() => setShowChangeClubVisibilityDialog(true)}>
                                {i18n.changeClubVisibility}
                            </Button>
                        </Grid>
                    )}
                    <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
                        <Button variant="contained" onClick={() => setShowTransferOwnershipDialog(true)}>
                            {i18n.transferOwnership}
                        </Button>
                    </Grid>
                    <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
                        <Button
                            style={{ background: "#d44336", color: "#fff" }}
                            color="secondary"
                            variant="contained"
                            onClick={() => setShowDeleteClubDialog(true)}
                        >
                            {i18n.deleteClub}
                        </Button>
                    </Grid>

                </Grid>
            </Box>

            {/* dialogs */}
            <EmbedDashboardDialog
                club={club}
                show={showEmbedDashboardDialog}
                onClose={() => setShowEmbedDashboardDialog(false)}
            />
            <ChangeClubVisibilityDialog
                club={club}
                ownerSettings={clubSettings}
                show={showChangeClubVisibilityDialog}
                onClose={() => setShowChangeClubVisibilityDialog(false)}
                onFetchUserProfile={onFetchUserProfile}
            />
            <TransferOwnershipDialog
                club={club}
                show={showTransferOwnershipDialog}
                onClose={() => setShowTransferOwnershipDialog(false)}
                onFetchUserProfile={onFetchUserProfile}
            />
            <DeleteClubDialog
                club={club}
                show={showDeleteClubDialog}
                onClose={() => setShowDeleteClubDialog(false)}
                onFetchUserProfile={onFetchUserProfile}
            />
            <Covid19Dialog
                club={club}
                show={showCovid19Dialog}
                onClose={() => setShowCovid19Dialog(false)}
            />
        </>
    );
};

export default OwnershipTab;
